import React, { useState, useEffect } from 'react'
import { FaTrash, FaMinus } from 'react-icons/fa'
import {
  Stack,
  Icon,
  Box,
  Heading,
  Text,
  FormControl,
  Input,
  Textarea,
  Select,
  Checkbox,
  Button,
  Alert,
  SimpleTable,
  SimpleModal,
  ConfirmButton,
  QrPopover,
  ReadOnlyCopyInput
} from '@/components/lib'
import RichText from '@/components/includes/RichText'
import utils from '../../../utils/utils'
import useStore from '@/store'

export default function Poll ({
  trial,
  stage,
  isPoll,
  setIsPoll,
  pollSettings,
  setPollSettings,
  customLinks,
  setCustomLinks,
  onSubmitRules,
  setOnSubmitRules
}) {
  const { team } = useStore()
  const canSendSms = utils.hasFeature(team, 'sms')
  const [selectedLink, setSelectedLink] = useState(null)
  const [generalLink, setGeneralLink] = useState('')
  const [editingMessage, setEditingMessage] = useState(null)

  useEffect(() => {
    if (stage) setGeneralLink(`${window.location.origin}/q/${stage._id}`)
  }, [stage])

  function createCustomLink () {
    const newLink = {
      id: utils.uuidv4(),
      name: 'My Custom Link',
      attributes: []
    }
    setCustomLinks([...(customLinks || []), newLink])
    setSelectedLink(newLink.id)
  }

  function updateCustomLink (linkId, link) {
    const newCustomLinks = customLinks?.map((l) =>
      l.id === linkId ? link : l
    )
    setCustomLinks(newCustomLinks)
  }

  const updateSettings = (update) => {
    setPollSettings((prev) => ({ ...prev, ...update }))
  }

  const addRule = () => {
    const newRule = {
      id: utils.uuidv4(),
      conditions: [],
      actions: []
    }
    setOnSubmitRules((prev) => [...(prev || []), newRule])
  }
  const deleteRule = (ruleId) => {
    setOnSubmitRules((prev) =>
      (prev || []).filter((rule) => rule.id !== ruleId)
    )
  }

  const updateRule = (ruleId, update) => {
    setOnSubmitRules((prev) =>
      (prev || []).map((rule) =>
        rule.id === ruleId ? { ...rule, ...update } : rule
      )
    )
  }

  return (
    <Stack gap={5}>
      <Box>
        <Heading as='h3' size='md'>
          Questionnaire mode
        </Heading>
        <Text color='fg.muted'>
          When in questionnaire mode, your stage form becomes publicly-available
          via a unique link. Questionnaires can be used for ad-hoc data
          collection (e.g. like a poll), or for participant screening.
        </Text>
      </Box>
      <Checkbox
        size='md'
        isChecked={isPoll}
        onChange={(e) => setIsPoll(e.checked)}
      >
        Enable questionnaire mode for this stage
      </Checkbox>

      {isPoll && (
        <>
          <Stack p={3} borderWidth='1px' rounded='md' bg='bg.subtle'>
            <Box>
              <Heading as='h3' size='md'>
                General link
              </Heading>
              <Text color='fg.muted'>
                The publicly-available link to complete this stage form.
              </Text>
            </Box>
            {stage
              ? (
                <Stack direction='row' alignItems='center'>
                  <ReadOnlyCopyInput value={generalLink} placeholder='—' />
                  <QrPopover
                    url={generalLink}
                    name={`${stage?.name}-general-link`}
                    size='sm'
                  />
                </Stack>
                )
              : (
                <Alert status='info' variant='left-accent'>
                  The link cannot be shown until the stage has been created.
                  Please save your stage and return to this page to view the link.
                </Alert>
                )}
          </Stack>

          <Stack gap={3} p={3} borderWidth='1px' rounded='md' bg='bg.subtle'>
            <Box>
              <Heading as='h3' size='md'>
                Custom links
              </Heading>
              <Text color='fg.muted'>
                Create custom links that you can share with specific groups of
                people.
              </Text>
            </Box>
            <Box>
              <Button size='sm' onClick={createCustomLink}>
                Add custom link
              </Button>
            </Box>
            {customLinks?.length > 0 && (
              <SimpleTable
                shadow='md'
                tableProps={{ variant: 'simple' }}
                headers={['Name', 'Attributes', 'Link', '']}
                rows={customLinks.map((link) => {
                  const url = `${generalLink}/access/${link.id}`
                  return [
                    link.name,
                    link.attributes?.length || 0,
                    <Box key={link.id}>
                      {stage
                        ? (
                          <Stack direction='row' alignItems='center'>
                            <ReadOnlyCopyInput
                              value={url}
                              placeholder='—'
                              size='sm'
                            />
                            {stage && (
                              <QrPopover
                                url={url}
                                name={`${stage?.name}-${link.name}`}
                                size='xs'
                              />
                            )}
                          </Stack>
                          )
                        : (
                          <Text fontSize='sm' color='fg.muted'>
                            Link cannot be displayed until stage is created.
                          </Text>
                          )}
                    </Box>,
                    <Stack direction='row' key={link.id} justifyContent='end'>
                      <Button
                        size='xs'
                        onClick={(e) => setSelectedLink(link.id)}
                      >
                        Edit
                      </Button>
                      <ConfirmButton
                        header='Really delete this link?'
                        body='This link will no longer work for anyone who already has it.'
                        onConfirm={(e) =>
                          setCustomLinks(
                            customLinks?.filter((l) => l.id !== link.id)
                          )}
                        actionColor='red'
                        trigger={
                          <Button size='xs' variant='ghost'>
                            <Icon>
                              <FaTrash />
                            </Icon>
                          </Button>
                        }
                      />
                    </Stack>
                  ]
                })}
              />
            )}
          </Stack>

          <CustomLinkEditor
            customLink={customLinks?.find((l) => l.id === selectedLink)}
            onComplete={(e) => setSelectedLink(null)}
            updateCustomLink={updateCustomLink}
          />

          <Stack p={3} borderWidth='1px' rounded='md' bg='bg.subtle'>
            <Box>
              <Heading as='h3' size='md'>
                Contact details
              </Heading>
              <Text color='fg.muted'>
                Specify what contact details you need to collect from
                participants as part of their submission.
              </Text>
            </Box>
            <Stack gap={2}>
              <Checkbox
                checked={pollSettings?.requireEmail}
                onChange={(e) => updateSettings({
                  requireEmail: e.checked,
                  // A detail that isn't collected can't be verified.
                  verifyEmail: e.checked ? pollSettings?.verifyEmail : false
                })}
                size='md'
              >
                Require an email address
              </Checkbox>
              {pollSettings?.requireEmail &&
                <Box pl={6}>
                  <Checkbox
                    checked={pollSettings?.verifyEmail}
                    onChange={(e) => updateSettings({ verifyEmail: e.checked })}
                    size='sm'
                  >
                    Verify it (we email a code that must be entered to submit)
                  </Checkbox>
                </Box>}
            </Stack>
            <Stack gap={2}>
              <Checkbox
                checked={pollSettings?.requirePhone}
                onChange={(e) => updateSettings({
                  requirePhone: e.checked,
                  verifyPhone: e.checked ? pollSettings?.verifyPhone : false
                })}
                size='md'
              >
                Require a phone number
              </Checkbox>
              {pollSettings?.requirePhone &&
                <Box pl={6}>
                  <Checkbox
                    checked={pollSettings?.verifyPhone}
                    onChange={(e) => updateSettings({ verifyPhone: e.checked })}
                    size='sm'
                    disabled={!canSendSms && !pollSettings?.verifyPhone}
                  >
                    Verify it (we text a code that must be entered to submit)
                  </Checkbox>
                  {!canSendSms &&
                    <Text fontSize='xs' color='fg.muted'>
                      Your team does not have SMS enabled, so no code could be
                      sent — and nobody would be able to submit.
                    </Text>}
                </Box>}
            </Stack>
            {(pollSettings?.verifyEmail || pollSettings?.verifyPhone) &&
              <Alert status='info'>
                Respondents will be asked to enter a code sent to the contact
                details they give, and cannot submit until they do. Text
                messages are charged to your trial wallet.
              </Alert>}
          </Stack>

          <Stack p={3} borderWidth='1px' rounded='md' bg='bg.subtle'>
            <Box>
              <Heading as='h3' size='md'>
                Rules
              </Heading>
              <Text color='fg.muted'>
                Configure automatic actions to take place when data is submitted
                to this stage form.
              </Text>
            </Box>
            {onSubmitRules?.length > 0
              ? (
                <SimpleTable
                  tableProps={{ variant: 'simple' }}
                  headers={['Rule', 'Conditions', 'Actions', '']}
                  rows={onSubmitRules?.map((rule, index) => [
                    <Text key={rule.id} fontWeight='bold'>
                      Rule {index + 1}
                    </Text>,
                    <Stack key={rule.id}>
                      {rule?.conditions?.length === 0 && (
                        <Text color='fg.muted' textAlign='center'>
                          No conditions set (will apply to all submissions)
                        </Text>
                      )}
                      {rule?.conditions?.map((condition, i) => (
                        <React.Fragment key={condition.id}>
                          {i > 0 && (
                            <Text
                              fontSize='xs'
                              fontWeight='bold'
                              color='fg.muted'
                              textAlign='center'
                            >
                              AND
                            </Text>
                          )}
                          <Stack
                            direction='row'
                            alignItems='center'
                            p={1}
                            bg='bg.muted'
                            rounded='md'
                          >
                            <Select
                              bg='bg.surface'
                              w='200px'
                              value={condition.operator}
                              onChange={(e) =>
                                updateRule(rule.id, {
                                  conditions: rule.conditions.map((c) =>
                                    c.id === condition.id
                                      ? { ...c, operator: e.target.value }
                                      : c
                                  )
                                })}
                              size='sm'
                              key={condition.id}
                            >
                              <option value='scoreEqual'>Score equal to...</option>
                              <option value='scoreGreater'>
                                Score greater than...
                              </option>
                              <option value='scoreLess'>Score less than...</option>
                            </Select>
                            {condition.operator?.includes('score') && (
                              <Input
                                bg='bg.surface'
                                size='xs'
                                w='90px'
                                type='number'
                                placeholder='Score'
                                value={condition.scoreThreshold || ''}
                                onChange={(e) =>
                                  updateRule(rule.id, {
                                    conditions: rule.conditions.map((c) =>
                                      c.id === condition.id
                                        ? { ...c, scoreThreshold: e.target.value }
                                        : c
                                    )
                                  })}
                              />
                            )}
                            <Button
                              size='xs'
                              variant='ghost'
                              onClick={() =>
                                updateRule(rule.id, {
                                  conditions: rule.conditions.filter(
                                    (c) => c.id !== condition.id
                                  )
                                })}
                            >
                              <Icon as={FaMinus} />
                            </Button>
                          </Stack>
                        </React.Fragment>
                      ))}
                      <Button
                        size='sm'
                        variant='ghost'
                        onClick={() =>
                          updateRule(rule.id, {
                            conditions: [
                              ...(rule.conditions || []),
                              { id: utils.uuidv4(), operator: 'scoreEqual' }
                            ]
                          })}
                      >
                        Add condition
                      </Button>
                    </Stack>,
                    <Stack key={rule.id}>
                      {!rule?.actions?.length && (
                        <Text color='fg.muted' textAlign='center'>
                          No actions will be taken
                        </Text>
                      )}
                      {rule?.actions?.map((action) => (
                        <Stack
                          direction='row'
                          alignItems='center'
                          key={action.id}
                          p={1}
                          bg='bg.muted'
                          rounded='md'
                        >
                          <Select
                            bg='bg.surface'
                            w='200px'
                            value={action.type}
                            onChange={(e) =>
                              updateRule(rule.id, {
                                actions: rule.actions.map((a) =>
                                  a.id === action.id
                                    ? { ...a, type: e.target.value }
                                    : a
                                )
                              })}
                            size='sm'
                            key={action.id}
                          >
                            <option value='markSuccessful'>
                              Mark as successful
                            </option>
                            <option value='markUnsuccessful'>
                              Mark as unsuccessful
                            </option>
                            <option value='allocateToUnallocatedParticipant'>
                              Allocate to next unallocated participant
                            </option>
                            <option value='emailParticipant'>
                              Email participant
                            </option>
                            <option value='smsParticipant'>
                              SMS participant
                            </option>
                            <option value='showOnScreenMessage'>
                              Show on-screen message
                            </option>
                          </Select>
                          {[
                            'emailParticipant',
                            'smsParticipant',
                            'showOnScreenMessage'
                          ].includes(action.type) && (
                            <Button
                              size='sm'
                              variant='outline'
                              onClick={() =>
                                setEditingMessage({
                                  ruleId: rule.id,
                                  actionId: action.id
                                })}
                            >
                              Edit message
                            </Button>
                          )}
                          <Button
                            size='xs'
                            variant='ghost'
                            onClick={() =>
                              updateRule(rule.id, {
                                actions: rule.actions.filter(
                                  (a) => a.id !== action.id
                                )
                              })}
                          >
                            <Icon as={FaMinus} />
                          </Button>
                        </Stack>
                      ))}
                      <Button
                        size='sm'
                        variant='ghost'
                        onClick={() =>
                          updateRule(rule.id, {
                            actions: [
                              ...(rule.actions || []),
                              { id: utils.uuidv4(), type: 'markSuccessful' }
                            ]
                          })}
                      >
                        Add action
                      </Button>
                    </Stack>,
                    <Box key={rule.id} display='flex' justifyContent='end'>
                      <ConfirmButton
                        header='Are you sure you want to delete this rule?'
                        trigger={
                          <Button size='sm' variant='ghost' colorPalette='gray'>
                            <Icon as={FaTrash} />
                          </Button>
                      }
                        onConfirm={() => deleteRule(rule.id)}
                      />
                    </Box>
                  ])}
                />
                )
              : (
                <Text color='fg.muted'>No rules have been set up yet.</Text>
                )}
            <Button size='sm' colorPalette='secondary' onClick={addRule}>
              Add a rule
            </Button>
            {onSubmitRules?.some((rule) =>
              rule.actions?.some(
                (action) => action.type === 'allocateToUnallocatedParticipant'
              )
            ) && (
              <Alert status='info' variant='left-accent' fontSize='sm'>
                <Box>
                  <Text fontWeight='bold'>Tip: Automatic messaging</Text>
                  <Text>
                    Since you've selected "Allocate to next unallocated
                    participant" as an action, you can also use the Messages
                    feature to automatically send messages to participants when
                    they complete this stage.
                  </Text>
                </Box>
              </Alert>
            )}
          </Stack>
        </>
      )}
      <MessageEditor
        trial={trial}
        editing={editingMessage}
        onSubmitRules={onSubmitRules}
        updateRule={updateRule}
        onClose={() => setEditingMessage(null)}
      />
    </Stack>
  )
}

function MessageEditor ({ trial, editing, onSubmitRules, updateRule, onClose }) {
  const action = editing
    ? onSubmitRules
      ?.find((r) => r.id === editing.ruleId)
      ?.actions?.find((a) => a.id === editing.actionId)
    : null

  const setMessage = (message) =>
    updateRule(editing.ruleId, {
      actions: onSubmitRules
        .find((r) => r.id === editing.ruleId)
        .actions.map((a) =>
          a.id === editing.actionId ? { ...a, message } : a
        )
    })

  const headers = {
    smsParticipant: 'Edit SMS message',
    showOnScreenMessage: 'Edit on-screen message'
  }

  return (
    <SimpleModal
      size='xl'
      header={headers[action?.type] || 'Edit email message'}
      isOpen={!!editing}
      onClose={onClose}
      body={
        action
          ? (
              action.type === 'showOnScreenMessage'
                ? (
                  <RichText
                    value={action.message || ''}
                    onChange={setMessage}
                    forType='trial'
                    forId={trial?._id}
                    allowImages
                  />
                  )
                : (
                  <Textarea
                    bg='bg.surface'
                    rows={8}
                    placeholder='Write your message...'
                    value={action.message || ''}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                  )
            )
          : null
      }
      footer={
        <Button colorPalette='primary' onClick={onClose}>
          Done
        </Button>
      }
    />
  )
}

function CustomLinkEditor ({ customLink, updateCustomLink, onComplete }) {
  function updateName (e) {
    updateCustomLink(customLink.id, { ...customLink, name: e.target.value })
  }

  function addAttribute () {
    updateCustomLink(customLink.id, {
      ...customLink,
      attributes: [...customLink.attributes, { name: '', value: '' }]
    })
  }

  function updateAttribute (index, attr) {
    const attributes = customLink.attributes.map((a, i) =>
      i === index ? attr : a
    )
    updateCustomLink(customLink.id, { ...customLink, attributes })
  }

  function removeAttribute (index) {
    const attributes = customLink.attributes.filter((a, i) => i !== index)
    updateCustomLink(customLink.id, { ...customLink, attributes })
  }

  return (
    <SimpleModal
      modalProps={{ size: 'xl' }}
      header='Manage custom link'
      isOpen={!!customLink}
      onClose={(e) => onComplete(null)}
      body={
        customLink
          ? (
            <Stack gap={5}>
              <FormControl
                id='name'
                label='Link name (so you can identify it later)'
              >
                <Input
                  type='text'
                  value={customLink.name}
                  onChange={updateName}
                />
              </FormControl>

              <Stack gap={3} borderWidth='1px' rounded='md' bg='bg.subtle' p={3}>
                <Box>
                  <Heading as='h3' size='md'>
                    Link attributes
                  </Heading>
                  <Text color='fg.muted'>
                    Add attributes to the link to automatically embed additional
                    data in responses when they are submitted.
                  </Text>
                </Box>
                {customLink.attributes?.length > 0 && (
                  <SimpleTable
                    tableProps={{ variant: 'simple' }}
                    headers={['Name', 'Value', '']}
                    rows={customLink.attributes.map((attr, index) => [
                      <Input
                        key={index}
                        bg='bg.surface'
                        size='sm'
                        type='text'
                        value={attr.name}
                        onChange={(e) =>
                          updateAttribute(index, {
                            ...attr,
                            name: e.target.value
                          })}
                      />,
                      <Input
                        key={index}
                        bg='bg.surface'
                        size='sm'
                        type='text'
                        value={attr.value}
                        onChange={(e) =>
                          updateAttribute(index, {
                            ...attr,
                            value: e.target.value
                          })}
                      />,
                      <Box key={index}>
                        <ConfirmButton
                          header='Really delete this attribute?'
                          onConfirm={(e) => removeAttribute(index)}
                          actionColor='red'
                          trigger={
                            <Button size='sm' variant='ghost'>
                              <Icon>
                                <FaTrash />
                              </Icon>
                            </Button>
                        }
                        />
                      </Box>
                    ])}
                  />
                )}
                <Button
                  colorPalette='primary'
                  variant='outline'
                  size='sm'
                  onClick={addAttribute}
                >
                  Add attribute
                </Button>
              </Stack>
            </Stack>
            )
          : null
      }
      footer={
        <>
          <Button colorPalette='primary' onClick={(e) => onComplete(null)}>
            Close
          </Button>
        </>
      }
    />
  )
}
